/**
 * usePwaInstall — auth-area install-prompt hook.
 *
 * Captures the `beforeinstallprompt` event so we can offer a manual
 * "Install Gleam Guru" button inside the authenticated staff area
 * (spec §3). Ordinary public visitors never see this control — it
 * lives inside AdminShell only.
 */
import { useEffect, useState, useCallback } from 'react';

const DISMISS_KEY = 'gg_pwa_install_dismissed_at';
const DISMISS_TTL_MS = 7 * 24 * 3600 * 1000; // one week

export const usePwaInstall = () => {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      const stamp = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (stamp && Date.now() - stamp < DISMISS_TTL_MS) setDismissed(true);
    } catch { /* noop */ }

    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    if (isStandalone) { setInstalled(true); return; }

    const onBefore = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener('beforeinstallprompt', onBefore);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBefore);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const prompt = useCallback(async () => {
    if (!deferred) return { outcome: 'unavailable' };
    deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    return choice;
  }, [deferred]);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setDismissed(true);
  }, []);

  const isIOS = /iP(hone|ad|od)/.test(navigator.userAgent) && !/CriOS|FxiOS/.test(navigator.userAgent);

  return {
    canPrompt:   !!deferred && !installed,
    installed,
    dismissed,
    prompt,
    dismiss,
    isIOS,
    // Show a banner when either a native prompt is queued OR iOS Safari
    // (which never fires beforeinstallprompt) and not yet dismissed.
    showBanner:  !installed && !dismissed && (!!deferred || isIOS),
  };
};
